import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import {
  Search,
  Clear,
  FilterAlt,
  LocalPharmacy,
  Medication,
  LocationOn,
  Group,
  Numbers,
  TableView,
  ViewModule,
  Assessment,
  Inventory,
  FolderOpen,
} from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import { useLocalizedData } from '../hooks/useLocalizedData';
import medicalData from "../data/moph_received.json";

const MedicalDonationsPage = () => {
  const { t } = useTranslation();
  const { getLocalizedValue } = useLocalizedData();
  const [filteredData, setFilteredData] = useState(medicalData);
  const [filters, setFilters] = useState({
    search: "",
    donor: "",
    type: "",
    governate: "",
  });
  const [viewMode, setViewMode] = useState("cards");
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      const result = medicalData.filter((item) => {
        return (
          (filters.search === "" ||
            item.type.toLowerCase().includes(filters.search.toLowerCase()) ||
            item.donor.toLowerCase().includes(filters.search.toLowerCase()) ||
            item.governate.toLowerCase().includes(filters.search.toLowerCase())) &&
          (filters.donor === "" || item.donor.includes(filters.donor)) &&
          (filters.type === "" || item.type.includes(filters.type)) &&
          (filters.governate === "" || item.governate === filters.governate)
        );
      });
      setFilteredData(result);
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setFilters({
      search: "",
      donor: "",
      type: "",
      governate: "",
    });
  };

  const uniqueDonors = [...new Set(medicalData.flatMap((item) =>
    item.donor.split(" - ").map((d) => d.trim())
  ))].filter(Boolean);
  
  // Create unique types with both languages
  const uniqueTypesData = medicalData.reduce((acc, item) => {
    if (item.type && !acc.find(t => t.type === item.type)) {
      acc.push({ type: item.type, type_ar: item.type_ar });
    }
    return acc;
  }, []);
  
  // Create unique governorates with both languages (if they exist)
  const uniqueGovernatesData = medicalData.reduce((acc, item) => {
    if (item.governate && !acc.find(g => g.governate === item.governate)) {
      acc.push({ governate: item.governate, governate_ar: item.governate_ar || item.governate });
    }
    return acc;
  }, []);
  
  const uniqueTypes = [...new Set(medicalData.map((item) => item.type))];
  const uniqueGovernates = [...new Set(medicalData.map((item) => item.governate))];

  const totalPackages = filteredData.reduce(
    (sum, item) => sum + (item["Quantity (package)"] || 0),
    0
  );

  const StatsCard = ({ title, value, icon, color }) => (
    <Card className="elegant-card" sx={{ height: '100%' }}>
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Avatar
          sx={{
            bgcolor: `${color}.main`,
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 2,
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="h4" sx={{ fontWeight: 700, color: `${color}.main`, mb: 1 }}>
          {value.toLocaleString()}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  const MedicalDonationCard = ({ item }) => (
    <Card className="elegant-card animate-fade-in-up" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {getLocalizedValue(item, 'type')}
            </Typography>
          </Box>
          <Avatar sx={{ bgcolor: 'primary.main', ml: 2 }}>
            <Medication />
          </Avatar>
        </Box>
        
        <Chip
          label={item.donor}
          size="small"
          sx={{
            background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
            color: 'white',
            fontWeight: 500,
            mb: 2,
            maxWidth: '100%',
          }}
        />

        {(item["Quantity (package)"] || item.governate) && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {item["Quantity (package)"] && (
              <Grid item xs={item.governate ? 6 : 12}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("Packages")}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {(item["Quantity (package)"] || 0).toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
            )}
            {item.governate && (
              <Grid item xs={item["Quantity (package)"] ? 6 : 12}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("Location")}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                    {item.governate}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
          <Chip
            icon={<LocalPharmacy />}
            label={t("Medical")}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box className="animate-fade-in-up">
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h2" 
          className="heading-primary"
          sx={{ 
            textAlign: 'center',
            mb: 2,
            background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {t("MOPH")}
        </Typography>
        <Typography 
          variant="h6" 
          className="text-elegant"
          sx={{ 
            textAlign: 'center',
            color: 'text.secondary',
            maxWidth: '800px',
            mx: 'auto',
            mb: 3
          }}
        >
          {t("Medical Donations and Distribution Report")}
        </Typography>

        {/* Information Requests Button */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            component="a"
            href="https://drive.google.com/drive/folders/1SjdxZyqWzJG7YtHCMGJwuGDeqWOdRdyS?usp=drive_link"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            startIcon={<FolderOpen />}
            sx={{
              background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(12, 159, 193, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0a7a96 0%, #0c9fc1 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(12, 159, 193, 0.4)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'white' }}>
                {t("Register of Information Requests")}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem', color: 'white' }}>
                {t("Official Responses from Administration")}
              </Typography>
            </Box>
          </Button>
        </Box>
        
        {/* MOPH Context */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            maxWidth: '900px', 
            mx: 'auto',
            background: 'linear-gradient(135deg, rgba(12, 159, 193, 0.05) 0%, rgba(77, 182, 209, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'primary.light',
            borderRadius: 3
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ 
              textAlign: 'center',
              color: 'text.primary',
              lineHeight: 1.7,
              fontSize: '1.1rem',
              mb: 3
            }}
          >
            {t("moph_description")}
          </Typography>
          
          {/* Statistics Reference Button */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to="/stats/moph"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: '0 4px 12px rgba(12, 159, 193, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0a7a96 0%, #0c9fc1 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(12, 159, 193, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
              startIcon={<Assessment />}
            >
              {t("View Detailed Statistics")}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatsCard
            title={t("Total Items")}
            value={filteredData.length}
            icon={<Inventory />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatsCard
            title={t("Total Packages")}
            value={totalPackages}
            icon={<LocalPharmacy />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatsCard
            title={t("Medical Types")}
            value={uniqueTypes.length}
            icon={<Medication />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatsCard
            title={t("Governorates")}
            value={uniqueGovernates.length}
            icon={<LocationOn />}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            <FilterAlt sx={{ verticalAlign: 'middle', mr: 1 }} />
            {t("Filter Medical Donations")}
          </Typography>
          <Box>
            <Button
              onClick={() => setViewMode(viewMode === "cards" ? "table" : "cards")}
              variant="outlined"
              sx={{ mr: 2 }}
              startIcon={viewMode === "cards" ? <TableView /> : <ViewModule />}
            >
              {viewMode === "cards" ? t("Table View") : t("Card View")}
            </Button>
            <Button
              onClick={handleReset}
              startIcon={<Clear />}
              variant="outlined"
            >
              {t("Clear Filters")}
            </Button>
          </Box>
        </Box>

        {/* Full-width search field */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label={t("Search Medical Donations")}
            variant="outlined"
            value={filters.search}
            onChange={(e) => handleChange("search", e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ color: "action.active", mr: 1 }} />,
              sx: {
                height: "56px",
                "& input": {
                  padding: "16.5px 14px",
                },
              },
            }}
          />
        </Box>

        {/* Select fields in a row */}
        <Box sx={{ width: "100%" }}>
          <Grid
            container
            spacing={2}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(3, 1fr)",
              },
            }}
          >
            <Grid item>
              <FormControl fullWidth>
                <InputLabel>{t("Type")}</InputLabel>
                <Select
                  value={filters.type}
                  label={t("Type")}
                  onChange={(e) => handleChange("type", e.target.value)}
                  sx={{ height: "56px" }}
                >
                  <MenuItem value="">{t("All Types")}</MenuItem>
                  {uniqueTypesData.map((typeObj) => (
                    <MenuItem key={typeObj.type} value={typeObj.type}>
                      {getLocalizedValue(typeObj, 'type')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl fullWidth>
                <InputLabel>{t("Donor")}</InputLabel>
                <Select
                  value={filters.donor}
                  label={t("Donor")}
                  onChange={(e) => handleChange("donor", e.target.value)}
                  sx={{ height: "56px" }}
                >
                  <MenuItem value="">{t("All Donors")}</MenuItem>
                  {uniqueDonors.map((donor) => (
                    <MenuItem key={donor} value={donor}>
                      {donor}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl fullWidth>
                <InputLabel>{t("Governorate")}</InputLabel>
                <Select
                  value={filters.governate}
                  label={t("Governorate")}
                  onChange={(e) => handleChange("governate", e.target.value)}
                  sx={{ height: "56px" }}
                >
                  <MenuItem value="">{t("All Governorates")}</MenuItem>
                  {uniqueGovernatesData.map((govObj) => (
                    <MenuItem key={govObj.governate} value={govObj.governate}>
                      {getLocalizedValue(govObj, 'governate')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Content */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : filteredData.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="text.secondary">
            {t("No medical donations match your filters")}
          </Typography>
          <Button variant="outlined" sx={{ mt: 2 }} onClick={handleReset}>
            {t("Clear Filters")}
          </Button>
        </Paper>
      ) : viewMode === "cards" ? (
        <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
          {filteredData.map((item, idx) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
              <MedicalDonationCard item={item} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Type (AR)</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Donor</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Governate</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Packages</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    "&:nth-of-type(odd)": { backgroundColor: "action.hover" },
                    "&:hover": { backgroundColor: "action.selected" },
                  }}
                >
                  <TableCell>{row.type}</TableCell>
                  <TableCell>{row.type_ar}</TableCell>
                  <TableCell>{row.donor}</TableCell>
                  <TableCell>{row.governate}</TableCell>
                  <TableCell align="right">{(row["Quantity (package)"] || 0).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default MedicalDonationsPage;
